import { useMemo } from 'react'
import DateInput from './DateInput'
import MultiSelect from './MultiSelect'
import { CONFIG_STATUS_MAP, FIRMWARE_STATUS_MAP } from './DeviceTableRow'
import { PlatformDevice } from '@/models/platformDevice'
import { exportExcel } from '@/utils/spreadsheetHelpers'
import { PLATFORM_DEVICE_EXPORT_TEMPLATE } from '@/constants/templateSchema'

interface PlatformDeviceFilters {
  serialNumber: string
  imei: string
  phoneNumber: string
  simSerialNumber: string
  type: string[]
  configurationStatus: string[],
  firmwareStatus: string[],
  dateAddedFrom: string
  dateAddedTo: string
}

interface PlatformDeviceFiltersProps {
  filters: PlatformDeviceFilters
  onFilterChange: (key: keyof PlatformDeviceFilters, value: string | string[]) => void
  onClearAllFilters: () => void
  devices: PlatformDevice[]
  filteredDevices: PlatformDevice[]
}

export default function PlatformDeviceFiltersComponent({
  filters,
  onFilterChange,
  onClearAllFilters,
  devices,
  filteredDevices
}: PlatformDeviceFiltersProps) {


  // Get unique values for dropdown filters
  const uniqueValues = useMemo(() => {
    return {
      simProviders: [...new Set(devices.map(d => d.simProviderName).filter(Boolean))],
      deviceTypes: [...new Set(devices.map(d => d.type).filter(Boolean))],
      configurationIds: [...new Set(devices.map(d => d.configurationId).filter(Boolean))],
      firmwares: [...new Set(devices.map(d => d.firmware).filter(Boolean))],
      configurationStatuses: Object.entries(CONFIG_STATUS_MAP).map(([key, value]) => ({
        key: key,
        label: value.label
      })),
      firmwareStatuses: Object.entries(FIRMWARE_STATUS_MAP).map(([key, value]) => ({
        key: key,
        label: value.label
      }))
    }
  }, [devices])

  const activeFiltersCount = useMemo(() => {
    let count = 0;
  
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'customFields' && value && typeof value === 'object' && !Array.isArray(value)) {
        const activeCustomFieldCount = Object.values(value).filter(
          (selections) => Array.isArray(selections) && selections.length > 0
        ).length;
        
        count += activeCustomFieldCount;
      } 
      else if (Array.isArray(value)) {
        if (value.length > 0) count++;
      } 
      else {
        if (value !== '' && value !== null && value !== undefined) count++;
      }
    });
  
    return count;
  }, [filters]);

  const exportToExcel = async () => {
    const dataRows = filteredDevices.map((device) => {
      return [
        device.serialNumber,
        device.imei,
        device.type,
        device.partnerName,
        device.phoneNumber,
        device.simSerialNumber,
        device.simProviderName,
        device.firmware,
        device.configurationName,
        device.dateAddedUtc,
        device.configurationStatus,
        device.firmwareStatus
      ];
    });
  
    exportExcel(PLATFORM_DEVICE_EXPORT_TEMPLATE.name, PLATFORM_DEVICE_EXPORT_TEMPLATE.columns, dataRows);
  }

  return (
    <div className="device-filters">
      <h3>
        Filter Devices
        {activeFiltersCount > 0 && (
          <span className="active-filters-count">{activeFiltersCount}</span>
        )}
      </h3>

      <div className="filters-grid">
        {/* Text-based filters */}
        <div className="filter-group">
          <label>Device Serial Number</label>
          <input
            type="text"
            className="filter-input"
            placeholder="Search by serial number..."
            value={filters.serialNumber}
            onChange={(e) => onFilterChange('serialNumber', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>IMEI Search</label>
          <input
            type="text"
            className="filter-input"
            placeholder="Search by IMEI..."
            value={filters.imei}
            onChange={(e) => onFilterChange('imei', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Phone Number Search</label>
          <input
            type="text"
            className="filter-input"
            placeholder="Search by phone number..."
            value={filters.phoneNumber}
            onChange={(e) => onFilterChange('phoneNumber', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>SIM Serial Search</label>
          <input
            type="text"
            className="filter-input"
            placeholder="Search by SIM serial..."
            value={filters.simSerialNumber}
            onChange={(e) => onFilterChange('simSerialNumber', e.target.value)}
          />
        </div>

        {/* Dropdown filters */}

        <div className="filter-group">
          <label>Device Type</label>
          <MultiSelect
            options={uniqueValues.deviceTypes
                .filter((provider): provider is string => !!provider)
                .map(type => ({
                  value: type,
                  label: type
                }))}
            selectedValues={filters.type}
            onChange={(values) => onFilterChange('type', values)}
            placeholder="All Device Types"
            className="filter-select"
          />
        </div>

        <div className="filter-group">
          <label>Configuration Status</label>
          <MultiSelect
            options={uniqueValues.configurationStatuses.map(status => ({
              value: status.key,
              label: status.label
            }))}
            selectedValues={filters.configurationStatus}
            onChange={(values) => onFilterChange('configurationStatus', values)}
            placeholder="All Statuses"
            className="filter-select"
          />
        </div>

        <div className="filter-group">
          <label>Firmware Status</label>
          <MultiSelect
            options={uniqueValues.firmwareStatuses.map(status => ({
              value: status.key,
              label: status.label
            }))}
            selectedValues={filters.firmwareStatus}
            onChange={(values) => onFilterChange('firmwareStatus', values)}
            placeholder="All Statuses"
            className="filter-select"
          />
        </div>

        {/* Date filters */}
        <div className="filter-group">
          <label>Date Added From</label>
          <DateInput
            value={filters.dateAddedFrom}
            onChange={(value) => onFilterChange('dateAddedFrom', value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>Date Added To</label>
          <DateInput
            value={filters.dateAddedTo}
            onChange={(value) => onFilterChange('dateAddedTo', value)}
            className="filter-input"
          />
        </div>
      </div>

      <div className="filters-actions">
        <button
          className="clear-filters-btn"
          onClick={onClearAllFilters}
          disabled={activeFiltersCount === 0}
        >
          Clear All Filters
        </button>
      </div>

      <div className="export-section">
        <button
          className="export-data-btn"
          onClick={exportToExcel}
          disabled={filteredDevices.length === 0}
        >
          Export Data
        </button>
      </div>
    </div>
  )
}

export type { PlatformDeviceFilters }